import React, { useState, useEffect } from "react";
import { Image as ImageIcon, Upload, X, Plus, Pencil, Trash2, Layout, Link as LinkIcon, Type } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { Button } from "../../components/ui/Button";
import ViewWebsiteButton from "../../components/ui/ViewWebsiteButton";
import FormField from "../../components/ui/FormField";
import { validateImage, resizeImage } from "../../lib/image";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import InfoContainer from "../../components/ui/InfoContainer";
import WebsiteSectionCard from "../../components/ui/WebsiteSectionCard";
import PageHeader from "../../components/ui/PageHeader";
import { Switch } from "../../components/ui/Switch";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { MotionConfig, AnimatePresence, motion } from "framer-motion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { cn } from "../../lib/utils";
import {
  getHeroSliders,
  createHeroSlider,
  updateHeroSlider,
  deleteHeroSlider,
  HeroSlider
} from "../../services/websitesetting";

const HeroSection = () => {
  const { subdomain } = useOutletContext<{ subdomain: string }>();
  const [sliders, setSliders] = useState<HeroSlider[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const toast = useToast();

  // Drawer State
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingSlider, setEditingSlider] = useState<HeroSlider | null>(null);

  // Form State
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [coverUpload, setCoverUpload] = useState<File | null>(null);

  // Content Fields
  const [haveContent, setHaveContent] = useState(true);
  const [heading, setHeading] = useState("");
  const [subheading, setSubheading] = useState("");
  const [content, setContent] = useState("");
  const [overflowVisible, setOverflowVisible] = useState(false);

  // Action Button Fields
  const [actionButtonType, setActionButtonType] = useState<"none" | "login" | "custom">("none");
  const [customLabel, setCustomLabel] = useState("");
  const [customUrl, setCustomUrl] = useState("");
  const [openIn, setOpenIn] = useState<"_self" | "_blank">("_self");

  // Delete Modal State
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const fetchHeroData = async () => {
    setIsLoading(true);
    try {
      const response = await getHeroSliders();
      if (response.success && response.data) {
        setSliders(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch hero settings:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHeroData();
  }, []);

  const resetForm = () => {
    setHeading("");
    setSubheading("");
    setContent("");
    setCoverImage(null);
    setCoverUpload(null);
    setEditingSlider(null);
    setHaveContent(true);
    setActionButtonType("none");
    setCustomLabel("");
    setCustomUrl("");
    setOpenIn("_self");
    setErrors({});
    setOverflowVisible(true);
  };

  const handleOpenDrawer = (slider?: HeroSlider) => {
    resetForm();
    if (slider) {
      setEditingSlider(slider);
      setCoverImage(slider.image_url);

      const hasContent = slider.have_content === "yes";
      setHaveContent(hasContent);
      setOverflowVisible(hasContent);

      if (hasContent) {
        setHeading((slider.heading || "").replace(/<[^>]*>?/gm, ''));
        setSubheading((slider.subheading || "").replace(/<[^>]*>?/gm, ''));
        setContent((slider.content || "").replace(/<[^>]*>?/gm, ''));

        // Map backend action_button to frontend state
        const rawAction = (slider.action_button || "none");
        const rawActionLower = rawAction.toLowerCase();

        if (rawActionLower === "login") {
          setActionButtonType("login");
        } else if (slider.url && slider.url.trim().length > 0) {
          setActionButtonType("custom");
          setCustomLabel(rawAction);
          setCustomUrl(slider.url);
        } else {
          setActionButtonType("none");
        }

        // Load open_tab for any active button type. Handle "new" from backend.
        const backendTab = slider.open_tab || "_self";
        setOpenIn(backendTab === "new" || backendTab === "_blank" ? "_blank" : "_self");
      }
    }
    setIsDrawerOpen(true);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await validateImage(file);
        setCoverUpload(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setCoverImage(e.target?.result as string);
        };
        reader.readAsDataURL(file);
        if (errors.image || errors.images) setErrors(prev => ({ ...prev, image: "", images: "" }));
      } catch (err: any) {
        toast.error(err.message || "Invalid image");
      }
    }
  };



  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleSave = async () => {
    if (!coverImage && !editingSlider) {
      toast.error("Image is required.");
      return;
    }

    if (haveContent) {
      if (!heading.trim()) {
        toast.error("Heading is required when content is enabled.");
        return;
      }
      if (actionButtonType === "custom") {
        if (!customLabel.trim()) {
          toast.error("Button Label is required for custom button.");
          return;
        }
        if (!customUrl.trim()) {
          toast.error("Button URL is required for custom button.");
          return;
        }
      }
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      if (editingSlider) {
        // UPDATE: Use FormData (Working New Logic)
        const formData = new FormData();
        formData.append("have_content", haveContent ? "yes" : "no");

        if (haveContent) {
          formData.append("heading", heading);
          formData.append("subheading", subheading);
          formData.append("slider_content", content);
          formData.append("content_alignment", "left");
          formData.append("actionButton", actionButtonType);

          // Decoupled open_tab: always send, mapped to 'new' if blank
          const tabInternal = openIn === "_blank" ? "new" : "_self";
          formData.append("tab", tabInternal);
          formData.append("open_tab", tabInternal);

          if (actionButtonType === "custom") {
            formData.append("custom_label", customLabel);
            formData.append("url", customUrl);
          } else {
            formData.append("custom_label", "");
            formData.append("url", "");
          }
        } else {
          formData.append("heading", "");
          formData.append("subheading", "");
          formData.append("slider_content", "");
          formData.append("actionButton", "none");
          formData.append("custom_label", "");
          formData.append("url", "");
        }

        if (coverUpload) {
          const resizedBlob = await resizeImage(coverUpload, 1920, 0.9, 1000);
          formData.append("images", resizedBlob, coverUpload.name);
        } else if (editingSlider.image) {
          formData.append("images", editingSlider.image);
        }

        await updateHeroSlider(editingSlider.id, formData);
        toast.success("Hero slide updated successfully!");

      } else {
        // CREATE: Use JSON + Base64 (Reverted Logic)
        let imageBase64 = null;

        if (coverUpload) {
          const resizedBlob = await resizeImage(coverUpload, 1920, 0.9, 1000);
          imageBase64 = await blobToBase64(resizedBlob);
        }

        const payload: any = {
          images: imageBase64,
          have_content: haveContent ? "yes" : "no",
        };

        if (haveContent) {
          payload.heading = heading;
          payload.subheading = subheading;
          payload.slider_content = content;
          payload.content_alignment = "left";

          payload.actionButton = actionButtonType;

          // Decoupled open_tab: always send, mapped to 'new' if blank
          const tabInternal = openIn === "_blank" ? "new" : "_self";
          payload.tab = tabInternal;
          payload.open_tab = tabInternal;

          if (actionButtonType === "custom") {
            payload.custom_label = customLabel;
            payload.url = customUrl;
          } else {
            payload.custom_label = "";
            payload.url = "";
          }
        } else {
          payload.heading = "";
          payload.subheading = "";
          payload.slider_content = "";
          payload.actionButton = "none";
          payload.custom_label = "";
          payload.url = "";
        }

        if (!imageBase64) {
          toast.error("Image is required for new slides.");
          setIsSubmitting(false);
          return;
        }
        await createHeroSlider(payload);
        toast.success("Hero slide created successfully!");
      }

      setIsDrawerOpen(false);
      fetchHeroData();
    } catch (error: any) {
      console.error("handleSave ERROR:", error);
      if (error.type === "serverValidation") {
        setErrors(error.errors);
        toast.error("Validation failed. Please check the requirements.");
      } else {
        toast.error("Failed to save hero slide.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (id: number) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    setIsSubmitting(true);
    try {
      await deleteHeroSlider(deleteId);
      toast.success("Hero slide deleted successfully!");
      fetchHeroData();
    } catch (err) {
      toast.error("Failed to delete hero slide.");
    } finally {
      setIsSubmitting(false);
      setDeleteId(null);
    }
  };

  return (
    <>
      {(isLoading || isSubmitting) && (
        <GlobalLoader
          initialPageLoad={isLoading || isSubmitting}
          description={isSubmitting ? "Saving changes..." : "Loading hero sliders..."}
        />
      )}
      <div className="space-y-5">
        <PageHeader
          title="Hero Section"
          description="Manage your website's main banner slides and images"
          variant="card"
          className="mb-5"
          mobileActionsInHeader={true}
          actions={<ViewWebsiteButton subdomain={subdomain} />}
        />

        <WebsiteSectionCard
          title="Hero Sliders"
          icon={Layout}
          actions={
            <Button
              onClick={() => handleOpenDrawer()}
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 text-xs font-medium flex items-center gap-2"
            >
              <Plus size={14} />
              Add New Slide
            </Button>
          }
        >
          <div className="space-y-6">
            <InfoContainer
              message="You can add multiple slides for your hero section. Each slide can have its own heading, subheading, and image to create a carousel effect on your website."
              iconSize={13}
            />

            {sliders.length === 0 && !isLoading ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <ImageIcon className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">No hero slides found</p>
                <p className="text-gray-400 text-sm mt-1">Click the 'Add New Slide' button to get started</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {sliders.map((slider) => (
                  <div key={slider.id} className="group relative bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all">
                    <div className="aspect-video relative overflow-hidden bg-gray-100">
                      <img
                        src={slider.image_url}
                        alt={slider.heading || "Hero Slide"}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2 flex gap-2">
                        <button
                          onClick={() => handleOpenDrawer(slider)}
                          className="p-2 bg-white text-[#885ABB] rounded-full hover:bg-[#885ABB] hover:text-white transition-colors shadow-lg"
                          title="Edit Slide"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(slider.id)}
                          className="p-2 bg-white text-red-500 rounded-full hover:bg-red-500 hover:text-white transition-colors shadow-lg"
                          title="Delete Slide"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="p-4 border-t border-gray-50">
                      <h4 className="font-bold text-gray-900 truncate">{(slider.heading || "No Title").replace(/<[^>]*>?/gm, '')}</h4>
                      <p className="text-sm text-gray-500 truncate mt-1">
                        {slider.have_content === 'yes'
                          ? (slider.subheading || "No Subheading").replace(/<[^>]*>?/gm, '')
                          : "Image Only"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </WebsiteSectionCard>
      </div>

      {/* Drawer */}
      <AnimatePresence>
        {isDrawerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[99]"
            onClick={() => setIsDrawerOpen(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isDrawerOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-full sm:w-[500px] bg-[#F9F9F9] shadow-[-10px_0_30px_rgba(0,0,0,0.05)] border-l border-gray-100 z-[100] flex flex-col origin-right"
          >
            {/* Close Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsDrawerOpen(false)}
              className="absolute lg:-left-14 top-4 lg:top-8 right-4 lg:right-auto w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-lg lg:shadow-[-4px_0_12px_rgba(0,0,0,0.1)] hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
            >
              <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
            </motion.button>

            {/* Header */}
            <div className="bg-white border-b border-gray-100 shadow-sm z-10 sticky top-0 px-6 py-6">
              <h2 className="text-xl sm:text-[27px] font-[600] text-[#402B69] leading-tight">
                {editingSlider ? "Edit Slide" : "Add New Slide"}
              </h2>
              <p className="text-[11px] text-gray-400 mt-1 font-semibold uppercase">
                Manage your hero section content
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8 pb-32">

              {/* Image Upload - Always Visible */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <ImageIcon size={16} className="text-[#885ABB]" />
                  <label className="text-sm font-bold text-gray-800">Banner Image</label>
                </div>

                <div className="relative group">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="drawer-hero-img"
                  />
                  <label
                    htmlFor="drawer-hero-img"
                    className={`w-full aspect-[2/1] rounded-xl border-2 border-dashed ${errors.image || errors.images ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-[#885ABB] bg-white'} flex flex-col items-center justify-center cursor-pointer transition-all overflow-hidden relative shadow-sm`}
                  >
                    {coverImage ? (
                      <>
                        <img
                          src={coverImage}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="bg-white text-gray-800 px-3 py-1.5 rounded-lg text-xs font-bold shadow-lg">Change Image</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                          <Upload className="h-6 w-6 text-gray-400 group-hover:text-[#885ABB]" />
                        </div>
                        <span className="text-sm font-semibold text-gray-600">Click to upload</span>
                        <span className="text-[10px] text-gray-400 mt-1">1920 × 1000px</span>
                      </>
                    )}
                  </label>
                  {coverImage && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setCoverImage(null);
                        setCoverUpload(null);
                      }}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition-colors z-10"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                {(errors.image || errors.images) && (
                  <p className="text-xs text-red-500 font-medium">{errors.image || errors.images}</p>
                )}
              </div>

              <hr className="border-gray-100" />

              {/* Have Content Toggle */}
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100">
                <div className="space-y-0.5">
                  <label className="text-sm font-bold text-[#402B69]">Show Text Content</label>
                  <p className="text-[10px] text-purple-600/70">Enable heading, description and buttons</p>
                </div>
                <Switch checked={haveContent} onCheckedChange={setHaveContent} />
              </div>

              <AnimatePresence initial={false}>
                {haveContent && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    onAnimationStart={() => setOverflowVisible(false)}
                    onAnimationComplete={() => setOverflowVisible(true)}
                    className={cn("space-y-6", overflowVisible ? "overflow-visible" : "overflow-hidden")}
                  >
                    <div className="space-y-4 pt-1">
                      <FormField
                        label="Heading"
                        name="heading"
                        value={heading}
                        onChange={(n, v) => setHeading(v)}
                        placeholder="Main headline"
                        error={errors.heading}
                      // // @ts-ignore
                      // icon={<Type size={14} className="text-gray-400" />}
                      />
                      <FormField
                        label="Subheading"
                        name="subheading"
                        value={subheading}
                        onChange={(n, v) => setSubheading(v)}
                        placeholder="Secondary line"
                        error={errors.subheading}
                      />
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-gray-700 uppercase">Description</label>
                        <textarea
                          className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] outline-none min-h-[80px] text-sm resize-none bg-white shadow-sm"
                          value={content}
                          onChange={(e) => setContent(e.target.value)}
                          placeholder="Brief description text..."
                        />
                      </div>
                    </div>

                    <div className="space-y-4 pt-2">
                      <div className="flex items-center gap-2 mb-2">
                        <LinkIcon size={16} className="text-[#885ABB]" />
                        <label className="text-sm font-bold text-gray-800">Action Button</label>
                      </div>

                      <Select value={actionButtonType} onValueChange={(v: any) => setActionButtonType(v)}>
                        <SelectTrigger className="w-full h-11 border-gray-200 bg-white rounded-xl text-sm font-medium shadow-sm">
                          <SelectValue placeholder="Select button type" />
                        </SelectTrigger>
                        <SelectContent className="bg-white z-[150]">
                          <SelectItem value="none">No Button</SelectItem>
                          <SelectItem value="login">Login Button</SelectItem>
                          <SelectItem value="custom">Custom Link</SelectItem>
                        </SelectContent>
                      </Select>

                      {actionButtonType === "custom" && (
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-4 animate-in fade-in slide-in-from-top-2">
                          <FormField
                            label="Button Label"
                            name="customLabel"
                            value={customLabel}
                            onChange={(n, v) => setCustomLabel(v)}
                            placeholder="e.g. Book Now"
                          />
                          <FormField
                            label="Button Link (URL)"
                            name="customUrl"
                            value={customUrl}
                            onChange={(n, v) => setCustomUrl(v)}
                            placeholder="https://..."
                          />
                        </div>
                      )}

                      <div className="space-y-1.5 pt-1">
                        <label className="text-xs font-bold text-gray-700 uppercase">Open In</label>
                        <Select value={openIn} onValueChange={(v: any) => setOpenIn(v)}>
                          <SelectTrigger className="w-full h-11 border-gray-200 bg-white rounded-xl text-sm font-medium shadow-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white z-[150]">
                            <SelectItem value="_self">Current Tab</SelectItem>
                            <SelectItem value="_blank">New Tab</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

            </div>

            {/* Footer - No Sticky, just bottom of flex container but nicely spaced */}
            <div className="bg-white p-6 border-t border-gray-100 flex items-center justify-end gap-3 z-10 shrink-0">
              <Button
                onClick={() => setIsDrawerOpen(false)}
                variant="ghost"
                className="text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSubmitting}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 h-10 shadow-lg shadow-purple-500/20"
              >
                {isSubmitting ? "Saving..." : (editingSlider ? "Update Slide" : "Create Slide")}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Hero Slide"
        message="Are you sure you want to delete this hero slide? This action cannot be undone."
        confirmText="Delete Slide"
        variant="danger"
      />
    </>
  );
};

export default HeroSection;
